import { MenuItem, MenuSub, Tooltip } from '@affine/component';
import { UpdatePlan } from '@affine/core/components/affine/update-plan';
import type { ExportType } from '@affine/core/components/hooks/affine/use-export-page';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import { PrinterIcon } from '@docnboard/icons/blocksuite/rc';
import {
  DocsIcon,
  HtmlIcon,
  InformationStrokeIcon,
  MdIcon,
  PdfIcon,
  PngIcon,
  UploadIcon,
} from '@docnboard/icons/rc';
import type { ReactNode } from 'react';
import { useCallback } from 'react';

import { transitionStyle } from './index.css';

interface ExportMenuItemProps<T> {
  onSelect: () => void;
  className?: string;
  type: T;
  prefixIcon: ReactNode;
  suffixIcon?: ReactNode;
  label: string;
  updatePlan?: boolean;
}

interface ExportProps {
  exportHandler: (type: ExportType) => void;
  converters?: ExportType[];
  pageMode?: 'page' | 'edgeless';
  className?: string;
}

const ExportMenuItemsDoc = ({
  exportHandler,
  converters,
  className = transitionStyle,
}: Omit<ExportProps, 'pageMode'>) => {
  const t = useI18n();

  return (
    <>
      <ExportMenuItem
        onSelect={() => exportHandler('dnb_to_docx')}
        className={className}
        type="docx"
        prefixIcon={<DocsIcon />}
        label={t['Export to Docx']()}
        updatePlan={!converters?.includes('dnb_to_docx')}
      />
      <ExportMenuItem
        onSelect={() => exportHandler('dnb_to_pdf')}
        className={className}
        type="pdf_save"
        prefixIcon={<PdfIcon />}
        label={t['Export to PDF']()}
        updatePlan={!converters?.includes('dnb_to_pdf')}
      />
      <ExportMenuItem
        onSelect={() => exportHandler('dnb_to_html')}
        className={className}
        type="html"
        prefixIcon={<HtmlIcon />}
        label={t['Export to HTML']()}
        updatePlan={!converters?.includes('dnb_to_html')}
      />
      <ExportMenuItem
        onSelect={() => exportHandler('dnb_to_md')}
        className={className}
        type="markdown"
        prefixIcon={<MdIcon />}
        label={t['Export to Markdown']()}
        updatePlan={!converters?.includes('dnb_to_md')}
      />
      <ExportMenuItem
        onSelect={() => exportHandler('snapshot')}
        className={className}
        type="snapshot"
        prefixIcon={<UploadIcon />}
        label={t['Export to Snapshot']()}
        updatePlan={!converters?.includes('snapshot')}
      />
    </>
  );
};

const ExportMenuItemsEdgeless = ({
  exportHandler,
  converters,
  className = transitionStyle,
}: Omit<ExportProps, 'pageMode'>) => {
  const t = useI18n();

  return (
    <>
      <ExportMenuItem
        onSelect={() => exportHandler('pdf')}
        className={className}
        type="pdf_save"
        prefixIcon={<PdfIcon />}
        suffixIcon={
          <Tooltip
            content={t['docnboard.export-menu.board-size-large.message']()}
          >
            <div>
              <InformationStrokeIcon />
            </div>
          </Tooltip>
        }
        label={t['Export to PDF']()}
        updatePlan={!converters?.includes('pdf')}
      />
      <ExportMenuItem
        onSelect={() => exportHandler('png')}
        className={className}
        type="png"
        prefixIcon={<PngIcon />}
        suffixIcon={
          <Tooltip
            content={t['docnboard.export-menu.board-size-large.message']()}
          >
            <div>
              <InformationStrokeIcon />
            </div>
          </Tooltip>
        }
        label={t['Export to PNG']()}
        updatePlan={!converters?.includes('png')}
      />
      <ExportMenuItem
        onSelect={() => exportHandler('snapshot')}
        className={className}
        type="snapshot"
        prefixIcon={<UploadIcon />}
        label={t['Export to Snapshot']()}
        updatePlan={!converters?.includes('snapshot')}
      />
    </>
  );
};

export function ExportMenuItem<T>({
  onSelect,
  className,
  type,
  prefixIcon,
  suffixIcon,
  label,
  updatePlan,
}: ExportMenuItemProps<T>) {
  return (
    <MenuItem
      className={className}
      data-testid={`export-to-${type}`}
      onSelect={onSelect}
      block
      prefixIcon={prefixIcon}
      disabled={updatePlan}
      suffixIcon={suffixIcon}
    >
      {label}
      {updatePlan && <UpdatePlan />}
    </MenuItem>
  );
}

export const PrintMenuItems = ({
  exportHandler,
  className = transitionStyle,
}: ExportProps) => {
  const t = useI18n();
  return (
    <ExportMenuItem
      onSelect={() => exportHandler('dnb_to_pdf')}
      className={className}
      type="pdf"
      prefixIcon={<PrinterIcon />}
      label={t['com.affine.export.print']()}
    />
  );
};

export const ExportMenuItems = ({
  exportHandler,
  converters,
  className = transitionStyle,
  pageMode = 'page',
}: ExportProps) => {
  if (pageMode === 'edgeless') {
    return (
      <ExportMenuItemsEdgeless
        exportHandler={exportHandler}
        converters={converters}
        className={className}
      />
    );
  }
  return (
    <ExportMenuItemsDoc
      exportHandler={exportHandler}
      className={className}
      converters={converters}
    />
  );
};

export const Export = ({ exportHandler, className, pageMode }: ExportProps) => {
  const t = useI18n();
  const items = (
    <>
      <ExportMenuItems
        exportHandler={exportHandler}
        className={className}
        pageMode={pageMode}
      />
      {/*  DNB_FIX скрыть печать */}
      {/*  {pageMode !== 'edgeless' && (
        <>
          <MenuSeparator />
          <PrintMenuItems exportHandler={exportHandler} className={className} />
        </>
      )} */}
    </>
  );
  const handleExportMenuOpenChange = useCallback((open: boolean) => {
    if (open) {
      track.$.header.docOptions.export();
    }
  }, []);

  return (
    <MenuSub
      items={items}
      triggerOptions={{
        className: transitionStyle,
        prefixIcon: <UploadIcon />,
        ['data-testid' as string]: 'export-menu',
      }}
      subOptions={{
        onOpenChange: handleExportMenuOpenChange,
      }}
    >
      {t.Export()}
    </MenuSub>
  );
};
